export interface DashboardPersona {
  id: string
  name: string
  icon: string
}

export const DASHBOARD_PERSONAS: DashboardPersona[] = [
  { id: 'owner', name: 'Owner', icon: 'mgc_user_star_fill' },
  { id: 'manager', name: 'Manager', icon: 'mgc_briefcase_fill' },
  { id: 'provider', name: 'Provider', icon: 'mgc_stethoscope_fill' },
  { id: 'hygienist', name: 'Hygienist', icon: 'mgc_hand_heart_fill' },
  { id: 'assistant', name: 'Assistant', icon: 'mgc_service_fill' },
  { id: 'front-desk', name: 'Front desk', icon: 'mgc_phone_fill' },
]

export interface DashboardOption {
  label: string
  value: string
}

export const DASHBOARD_DATE_RANGE_OPTIONS: DashboardOption[] = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Yearly', value: 'yearly' },
]

export const DASHBOARD_REFRESH_INTERVAL_OPTIONS: DashboardOption[] = [
  { label: '1 minute', value: '1' },
  { label: '5 minutes', value: '5' },
  { label: '15 minutes', value: '15' },
  { label: '30 minutes', value: '30' },
  { label: '1 hour', value: '60' },
]

export type DashboardWidgetCategory = 'financial' | 'operations' | 'performance'

export const DASHBOARD_WIDGET_TABS: {
  id: DashboardWidgetCategory
  label: string
}[] = [
  { id: 'financial', label: 'Financial' },
  { id: 'operations', label: 'Operations' },
  { id: 'performance', label: 'Performance' },
]

export interface DashboardWidget {
  id: string
  name: string
  description: string
  defaultEnabled: boolean
}

export const DASHBOARD_WIDGETS: Record<
  DashboardWidgetCategory,
  DashboardWidget[]
> = {
  financial: [
    {
      id: 'gross-production',
      name: 'Gross production',
      description: 'Total production before adjustments & write-offs',
      defaultEnabled: true,
    },
    {
      id: 'net-production',
      name: 'Net production',
      description: 'Production after adjustments & write-offs',
      defaultEnabled: false,
    },
    {
      id: 'gross-collection',
      name: 'Gross collection',
      description:
        'Highlights urgent items like unconfirmed appointments or overdue A/R',
      defaultEnabled: true,
    },
    {
      id: 'net-collection',
      name: 'Net collection',
      description: 'Production after adjustments & write-offs',
      defaultEnabled: false,
    },
    {
      id: 'insurance-patient-ar',
      name: 'Insurance & patient AR',
      description: 'Daily, weekly & monthly production totals',
      defaultEnabled: true,
    },
    {
      id: 'ar-aging',
      name: 'AR aging',
      description: 'Daily, weekly & monthly production totals',
      defaultEnabled: true,
    },
    {
      id: 'overhead-percent',
      name: 'Overhead %',
      description: 'Outstanding insurance claims aging',
      defaultEnabled: true,
    },
    {
      id: 'profit-margin-percent',
      name: 'Profit margin %',
      description: 'Claims ready to send but not yet submitted',
      defaultEnabled: false,
    },
    {
      id: 'production-per-provider',
      name: 'Production per provider',
      description: 'Active patients due for recare',
      defaultEnabled: true,
    },
    {
      id: 'production-per-visit',
      name: 'Production per visit',
      description: 'Presented vs. accepted treatment plans',
      defaultEnabled: true,
    },
    {
      id: 'collection-per-visit',
      name: 'Collection per visit',
      description: 'Patients with outstanding balances past due',
      defaultEnabled: false,
    },
    {
      id: 'forecasted-revenue',
      name: 'Forecasted revenue',
      description: 'Patients with outstanding balances past due',
      defaultEnabled: false,
    },
    {
      id: 'scheduled-production',
      name: 'Scheduled production',
      description: 'Daily, weekly & monthly production totals',
      defaultEnabled: true,
    },
    {
      id: 'revenue-growth',
      name: 'Revenue growth',
      description: 'Claims ready to send but not yet submitted',
      defaultEnabled: false,
    },
  ],
  operations: [
    {
      id: 'schedule-fill-rate',
      name: 'Schedule fill rate',
      description:
        'Highlights urgent items like unconfirmed appointments or overdue A/R',
      defaultEnabled: true,
    },
    {
      id: 'open-chair-time',
      name: 'Open chair time',
      description: 'Production after adjustments & write-offs',
      defaultEnabled: false,
    },
    {
      id: 'provider-hygiene-utilization-rate',
      name: 'Provider/hygiene utilization rate',
      description:
        'Highlights urgent items like unconfirmed appointments or overdue A/R',
      defaultEnabled: true,
    },
    {
      id: 'confirmation-rate',
      name: 'Confirmation rate',
      description: 'Production after adjustments & write-offs',
      defaultEnabled: false,
    },
    {
      id: 'no-show-rate',
      name: 'No-show rate',
      description: 'Daily, weekly & monthly production totals',
      defaultEnabled: true,
    },
    {
      id: 'same-day-cancellation-rate',
      name: 'Same-day cancellation rate',
      description: 'Daily, weekly & monthly production totals',
      defaultEnabled: true,
    },
    {
      id: 'appointment-overrun-rate',
      name: 'Appointment overrun rate',
      description: 'Outstanding insurance claims aging',
      defaultEnabled: true,
    },
    {
      id: 'operatory-turnover-rate',
      name: 'Operatory turnover rate',
      description: 'Claims ready to send but not yet submitted',
      defaultEnabled: false,
    },
    {
      id: 'operatory-downtime',
      name: 'Operatory downtime',
      description: 'Claims ready to send but not yet submitted',
      defaultEnabled: false,
    },
    {
      id: 'recall-rate',
      name: 'Recall rate',
      description: 'Active patients due for recare',
      defaultEnabled: true,
    },
    {
      id: 'treatment-schedule-rate',
      name: 'Treatment schedule rate',
      description: 'Presented vs. accepted treatment plans',
      defaultEnabled: true,
    },
    {
      id: 'unscheduled-rate',
      name: 'Unscheduled rate',
      description: 'Patients with outstanding balances past due',
      defaultEnabled: false,
    },
    {
      id: 'call-answered',
      name: 'Call answered',
      description: 'Patients with outstanding balances past due',
      defaultEnabled: false,
    },
    {
      id: 'missed-call-rate',
      name: 'Missed call rate',
      description: 'Daily, weekly & monthly production totals',
      defaultEnabled: true,
    },
  ],
  performance: [
    {
      id: 'case-acceptance-rate',
      name: 'Case acceptance rate',
      description:
        'Highlights urgent items like unconfirmed appointments or overdue A/R',
      defaultEnabled: true,
    },
    {
      id: 'large-case-acceptance-rate',
      name: 'Large case acceptance rate',
      description: 'Production after adjustments & write-offs',
      defaultEnabled: false,
    },
    {
      id: 'same-day-tx-plan',
      name: 'Same day tx plan',
      description:
        'Highlights urgent items like unconfirmed appointments or overdue A/R',
      defaultEnabled: true,
    },
    {
      id: 'treatment-plan-completion-rate',
      name: 'Treatment plan completion rate',
      description: 'Production after adjustments & write-offs',
      defaultEnabled: false,
    },
    {
      id: 'diagnosis-rate',
      name: 'Diagnosis rate',
      description: 'Daily, weekly & monthly production totals',
      defaultEnabled: true,
    },
    {
      id: 'treatment-plan-deferral',
      name: 'Treatment plan deferral',
      description: 'Daily, weekly & monthly production totals',
      defaultEnabled: true,
    },
    {
      id: 'patient-reactivation-rate',
      name: 'Patient reactivation rate',
      description: 'Outstanding insurance claims aging',
      defaultEnabled: true,
    },
    {
      id: 'patient-retention-rate',
      name: 'Patient retention rate',
      description: 'Claims ready to send but not yet submitted',
      defaultEnabled: false,
    },
    {
      id: 'new-patient-conversion-rate',
      name: 'New patient conversion rate',
      description: 'Claims ready to send but not yet submitted',
      defaultEnabled: false,
    },
    {
      id: 'patient-reappointment-rate',
      name: 'Patient reappointment rate',
      description: 'Active patients due for recare',
      defaultEnabled: true,
    },
    {
      id: 'patient-satisfaction-rate',
      name: 'Patient satisfaction rate',
      description: 'Patients with outstanding balances past due',
      defaultEnabled: false,
    },
    {
      id: 'perio-diagnosis-rate',
      name: 'Perio diagnosis rate',
      description: 'Presented vs. accepted treatment plans',
      defaultEnabled: true,
    },
    {
      id: 'clinical-protocol',
      name: 'Clinical protocol',
      description: 'Patients with outstanding balances past due',
      defaultEnabled: false,
    },
    {
      id: 'missed-call-rate-performance',
      name: 'Missed call rate',
      description: 'Daily, weekly & monthly production totals',
      defaultEnabled: true,
    },
  ],
}

export interface DashboardDisplayPreferences {
  dateRange: string
  refreshInterval: string
  showGoalProgress: boolean
  showAlertBanners: boolean
}

export interface DashboardPersonaConfig {
  display: DashboardDisplayPreferences
  widgets: Record<string, boolean>
}

const buildDefaultWidgets = (): Record<string, boolean> => {
  const widgets: Record<string, boolean> = {}
  DASHBOARD_WIDGET_TABS.forEach(({ id }) => {
    DASHBOARD_WIDGETS[id].forEach((widget) => {
      widgets[widget.id] = widget.defaultEnabled
    })
  })
  return widgets
}

export const buildDefaultPersonaConfig = (): DashboardPersonaConfig => ({
  display: {
    dateRange: 'weekly',
    refreshInterval: '5',
    showGoalProgress: true,
    showAlertBanners: false,
  },
  widgets: buildDefaultWidgets(),
})

export const buildDefaultDashboardConfigs = (): Record<
  string,
  DashboardPersonaConfig
> => {
  const configs: Record<string, DashboardPersonaConfig> = {}
  DASHBOARD_PERSONAS.forEach((persona) => {
    configs[persona.id] = buildDefaultPersonaConfig()
  })
  return configs
}
